/**
 * Season Manager — seasons on the map (polygons, geojson-vt fill, centroid markers).
 */

import {inject, Injectable, NgZone} from '@angular/core';
import geojsonvt from 'geojson-vt';
import {TranslateService} from '@ngx-translate/core';
import {Field, Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {Filter} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {BehaviorSubject, Observable, race, Subject, Subscription, timer,} from 'rxjs';
import {debounceTime, filter, map, mergeMap, take, tap} from 'rxjs/operators';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {GisCircleMarker} from '../interfaces/gis-component.interfaces';
import {createRegionCircleMarkers} from '@app/utils/circle-marker.util';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {findFeatureAtPoint, latLngToTileCoords, latLngToTilePixel,} from '@app/utils/vector-tile.utils';
import {MapClickEvent, MapMoveEvent} from '@components/openstreetmap/interfaces';
import {filterByViewportBounds} from './gis-map-viewport-filter.util';
import {escapePopupHtml} from './gis-popup-html.util';
import {buildVtOriginalFeaturesByNumericId} from './gis-vt-feature-index.util';
import {paintVtPolygonOnCanvasTile} from './gis-vt-polygon-canvas.util';
import {OSM_POLYGON_FILL_VISIBILITY_EVENT} from '@components/openstreetmap/constants/map-polygon-fill-visibility.state';
import {
  SEASON_PLAN_FACT_MISMATCH_COLOR,
  SEASON_PLAN_FACT_MISMATCH_STROKE,
  SEASON_SURVEY_BLOCKED_POLYGON_FILL,
  SEASON_SURVEY_BLOCKED_POLYGON_STROKE,
} from './season-map-legend.constants';

declare var L: any;

const SEASON_VT_TILE_EXTENT = 4096;
/** Viewport padding when filtering seasons (same as fields). */
const SEASON_VIEWPORT_BOUNDS_PAD = 0.08;
const SEASON_POLYGON_STROKE = '#ffffff';
/** Default fill when tags omit fillColor (canvas VT). */
const SEASON_VT_DEFAULT_FILL = '#4287f5';
/** Same red as ground-survey `map.component.ts` centre marker (`updateCenterMarker`). */
const SEASON_MONITORING_GROUND_SURVEY_PIN_FILL = '#ff0000';
const SEASON_MANUAL_SURVEY_BLOCK_REASON_PIN_FILL = SEASON_SURVEY_BLOCKED_POLYGON_FILL;
/**
 * Season VT pane z-index: keep below dynamicPolygonsPane (900) so plan/fact SVG stripes
 * stay visible above the canvas layer.
 */
const SEASON_VECTOR_TILE_PANE_Z_INDEX = '878';
const SEASON_GEOJSON_VT_OPTIONS = {
  maxZoom: 18,
  extent: SEASON_VT_TILE_EXTENT,
  buffer: 64,
  lineMetrics: false,
  promoteId: 'seasonVtId' as const,
  generateId: false,
  indexMaxZoom: 5,
  indexMaxPoints: 100000,
} as const;

@Injectable({providedIn: 'root'})
export class SeasonManager {
  private translate = inject(TranslateService);
  public seasonService = inject(SeasonService);
  private regionManager = inject(RegionManagerService);
  private mapInstance: OpenStreetMapComponent | null = null;
  private cropCacheService = inject(CropCacheService);
  private ngZone = inject(NgZone);


  // Store data
  public seasons$ = new BehaviorSubject<Season[]>([]);

  // Events
  public seasonMarkerClick$ = new Subject<{ lat: number; lng: number; data: any }>();
  public seasonPolygonClick$ = new Subject<any>();

  private seasonsDisplaySub?: Subscription;

  /** Matches GIS fields plan/fact filter (cropType): fact | plan | both */
  private planFactDisplayMode: 'fact' | 'plan' | 'both' = 'fact';

  /** plan → isPlanned true, fact → false, both → no filter. */
  static buildIsPlannedFilterForPlanFactMode(mode: string | undefined | null): Filter | null {
    if (mode === 'plan') {
      return {
        filterField: 'isPlanned',
        filterOperator: FilterOperator.Equals,
        filterValue: true,
      };
    }
    if (mode === 'fact') {
      return {
        filterField: 'isPlanned',
        filterOperator: FilterOperator.Equals,
        filterValue: false,
      };
    }
    return null;
  }

  /** Zoom [12, 14): centroid / point markers only; ≥14 polygons + markers (no API reload on zoom tier). */
  private static readonly ZOOM_DETAIL_MIN = 12;
  private static readonly ZOOM_POLYGONS_ONLY_MIN = 14;

  private currentZoom = 13;
  private langSubscription?: Subscription;

  private mapSeasonViewportRedrawSub?: Subscription;
  /** Tracks ids of markers and polygons added by this manager. */
  private managedMarkerIds: Set<string> = new Set();
  private managedPolygonIds: Set<string | number> = new Set();

  private seasonsVtLayer: any = null;
  private seasonsVtPointer: {
    tileIndex: any;
    featuresById: Map<number, any>;
  } | null = null;
  private seasonsVtOpId = 0;
  private seasonsVtMapClickSub?: Subscription;

  private polygonFillLeafletMap: any = null;
  private readonly onOsmPolygonFillVisibility = (): void => {
    try {
      this.seasonsVtLayer?.redraw?.();
    } catch {
      /* ignore */
    }
  };

  /** Increments once per viewport paint (`updateDisplay`); settles when polygons/markers (+ VT tiles if any) are done. */
  private displayPaintSeq = 0;
  private lastPaintSettledSeq = 0;
  private readonly paintSettled$ = new Subject<number>();
  /** When a geojson-vt layer is rendering asynchronously, the settle seq tied to its `updateDisplay` cycle. */
  private vtSettleSeqPending: number | null = null;

  setPlanFactDisplayMode(mode: string | undefined | null): void {
    if (mode === 'plan' || mode === 'both') {
      this.planFactDisplayMode = mode;
    } else {
      this.planFactDisplayMode = 'fact';
    }
    if (this.mapInstance && this.seasons$.value?.length && this.currentZoom >= SeasonManager.ZOOM_DETAIL_MIN) {
      this.updateDisplay();
    }
  }


  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapSeasonViewportRedrawSub?.unsubscribe();
    this.mapSeasonViewportRedrawSub = undefined;
    this.seasonsVtMapClickSub?.unsubscribe();
    this.seasonsVtMapClickSub = undefined;

    this.mapInstance = mapInstance;
    try {
      const z = mapInstance.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.currentZoom = z;
      }
    } catch {
      /* ignore */
    }

    const leafletMap = mapInstance.getMapInstance?.();
    if (this.polygonFillLeafletMap && this.polygonFillLeafletMap !== leafletMap) {
      try {
        this.polygonFillLeafletMap.off(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
      } catch {
        /* ignore */
      }
    }
    this.polygonFillLeafletMap = leafletMap ?? null;
    try {
      leafletMap?.on?.(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
    } catch {
      /* ignore */
    }

    if (!this.seasonsDisplaySub) {
      this.seasonsDisplaySub = this.seasons$.subscribe(() => {
        this.updateDisplay();
      });
    }
    this.setupSeasonViewportRedrawListeners();
    this.setupLangRefresh();

    if (mapInstance.mapClick$) {
      this.seasonsVtMapClickSub = mapInstance.mapClick$.subscribe(ev =>
        this.handleSeasonVtMapClick(ev),
      );
    }
  }

  private setupLangRefresh(): void {
    if (this.langSubscription) {
      return;
    }
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      if (!this.mapInstance || !this.seasons$.value?.length) {
        return;
      }
      if (this.currentZoom < SeasonManager.ZOOM_DETAIL_MIN) {
        return;
      }
      this.updateDisplay();
    });
  }

  /** Same debounce pattern as FieldManager: avoid double redraw on zoom. */
  private setupSeasonViewportRedrawListeners(): void {
    if (!this.mapInstance || this.mapSeasonViewportRedrawSub) {
      return;
    }

    const m = this.mapInstance;
    const sub = new Subscription();

    if (m.mapZoomChange$) {
      sub.add(
        m.mapZoomChange$.subscribe((zoom: number) => {
          // No updateDisplay here: mapMove$ debounce covers post-zoom moveend.
          this.currentZoom = zoom;
        }),
      );
    }

    if (m.mapMove$) {
      sub.add(
        m.mapMove$.pipe(debounceTime(120)).subscribe((ev: MapMoveEvent) => {
          if (typeof ev.zoom === 'number') {
            this.currentZoom = ev.zoom;
          }
          this.updateDisplay();
        }),
      );
    }

    this.mapSeasonViewportRedrawSub = sub;
  }

  private querySeasonVtAt(
    lat: number,
    lng: number,
  ): { season: Season; origProperties?: any } | null {
    const vt = this.seasonsVtPointer;
    if (!vt || !this.mapInstance) {
      return null;
    }

    const leafletMap = this.mapInstance.getMapInstance?.();
    if (!leafletMap) {
      return null;
    }

    const zoom = Math.floor(leafletMap.getZoom());
    const tc = latLngToTileCoords(lat, lng, zoom);
    const tileData = vt.tileIndex.getTile(tc.z, tc.x, tc.y);
    if (!tileData?.features?.length) {
      return null;
    }

    const pixel = latLngToTilePixel(lat, lng, tc.z, tc.x, tc.y, SEASON_VT_TILE_EXTENT);
    const hitRes = findFeatureAtPoint(tileData.features, pixel.x, pixel.y, vt.featuresById, 50);
    if (!hitRes) {
      return null;
    }

    const orig = hitRes.originalFeature;
    const tf = hitRes.tileFeature;
    const season =
      (orig?.properties?._season as Season | undefined) ??
      ((((tf?.tags ?? tf) as any)?._season as Season | undefined) ?? null);

    if (!season) {
      return null;
    }

    return {season, origProperties: orig?.properties};
  }

  private handleSeasonVtMapClick(ev: MapClickEvent): void {
    if (
      this.currentZoom < SeasonManager.ZOOM_POLYGONS_ONLY_MIN ||
      this.currentZoom < SeasonManager.ZOOM_DETAIL_MIN ||
      !this.seasonsVtPointer
    ) {
      return;
    }
    const hit = this.querySeasonVtAt(ev.lat, ev.lng);
    if (!hit) {
      return;
    }

    this.seasonPolygonClick$.next({
      properties: hit.origProperties,
      lat: ev.lat,
      lng: ev.lng,
      data: {seasonId: hit.season.id, season: hit.season},
    });
  }

  /** Numeric id for geojson-vt `promoteId` and map hit-testing (string ids are hashed). */
  private seasonVtNumericId(season: Season): number {
    const raw = season.id as unknown;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === 'string' && raw.trim().length) {
      const n = Number(raw);
      if (Number.isFinite(n) && String(n) === raw.trim()) {
        return n;
      }
    }
    const nFallback = Number(raw);
    if (Number.isFinite(nFallback)) {
      return nFallback;
    }
    const s = String(raw ?? '');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    const id = Math.abs(h) >>> 0;
    return id || 1;
  }

  private cropDisplayName(cropId: string | null | undefined): string {
    if (cropId == null || String(cropId).length === 0) {
      return escapePopupHtml('—');
    }
    const name =
      this.cropCacheService.getCropNameById(String(cropId), true) ??
      String(cropId);
    return escapePopupHtml(name);
  }

  private getSeasonPlannedCropId(season: Season): string | null | undefined {
    const seasonAny = season as any;
    return seasonAny?.plannedCropId ?? seasonAny?.PlannedCropId;
  }

  private getSeasonPlannedCropName(season: Season): string | null | undefined {
    const seasonAny = season as any;
    return seasonAny?.plannedCropName ?? seasonAny?.PlannedCropName;
  }

  private plannedCropDisplayName(season: Season): string {
    const plannedName = this.getSeasonPlannedCropName(season);
    if (plannedName != null && String(plannedName).trim().length > 0) {
      return escapePopupHtml(String(plannedName));
    }
    const plannedId = this.getSeasonPlannedCropId(season);
    return this.cropDisplayName(plannedId as string | null | undefined);
  }

  private formatPopupAreaHa(area: unknown): string {
    if (area == null || area === '') {
      return escapePopupHtml('—');
    }
    const n = Number(area);
    if (!Number.isFinite(n)) {
      return escapePopupHtml(String(area));
    }
    return escapePopupHtml(n.toFixed(2));
  }

  private resolvePlanSeason(season: Season): Season | null {
    const seasonAny = season as any;
    if (seasonAny.isPlanned === true) {
      return season;
    }
    const planSeasonId = seasonAny.planSeasonId ?? seasonAny.PlanSeasonId;
    if (planSeasonId == null) {
      return null;
    }
    return this.seasons$.value.find(s => String(s.id) === String(planSeasonId)) ?? null;
  }

  private isFactSeason(season: Season): boolean {
    const seasonAny = season as any;
    if (seasonAny.isFact === true) {
      return true;
    }
    if (seasonAny.isPlanned === true) {
      return false;
    }
    return seasonAny.planSeasonId != null || seasonAny.PlanSeasonId != null;
  }

  private getLinkedFactSeasons(planSeasonId: string | undefined | null): Season[] {
    if (planSeasonId == null) {
      return [];
    }
    const planId = String(planSeasonId);
    return this.seasons$.value
      .filter(s => {
        const sAny = s as any;
        if (String(sAny.planSeasonId ?? sAny.PlanSeasonId) !== planId) {
          return false;
        }
        return this.isFactSeason(s) && sAny.cropId != null && String(sAny.cropId).length > 0;
      })
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }

  private buildFactCropRowsHtml(factSeasons: Season[]): string {
    if (factSeasons.length === 0) {
      const label = escapePopupHtml(this.translate.instant('FACT_CROP_N', { n: 1 }));
      return `<br><small>${label}: ${escapePopupHtml('—')}</small>`;
    }
    return factSeasons
      .map((factSeason, index) => {
        const label = escapePopupHtml(this.translate.instant('FACT_CROP_N', { n: index + 1 }));
        return `<br><small>${label}: ${this.cropDisplayName(factSeason.cropId as string | undefined)}</small>`;
      })
      .join('');
  }

  private getSeasonDisplayTitle(season: Season): string {
    const seasonAny = season as any;
    const fieldName = seasonAny.fieldName ?? seasonAny.FieldName;
    if (fieldName != null && String(fieldName).trim().length > 0) {
      return String(fieldName).trim();
    }
    return season.title || this.translate.instant('SEASON');
  }

  private buildSeasonPopupHtml(season: Season): string {
    const seasonAny = season as any;
    const planFactLine = this.buildPlanFactPopupLines(season);
    const title = this.getSeasonDisplayTitle(season);
    const haUnit = escapePopupHtml(this.translate.instant('HA'));
    const monitoring = !!(seasonAny.monitoring ?? seasonAny.isMonitoring);
    const hasManualSurveyBlockReason = this.hasManualSurveyBlockReason(season);
    const hasRemoteSurveyBlockReason = this.hasRemoteSurveyBlockReason(season);
    const monLabel = escapePopupHtml(this.translate.instant('IS_MONITORING'));
    const monVal = escapePopupHtml(this.translate.instant(monitoring ? 'Yes' : 'No'));
    const manualSurveyLine = hasManualSurveyBlockReason
      ? `<br><small><b>${escapePopupHtml(this.translate.instant('MANUAL_SURVEY_BLOCKERS'))}</b></small>`
      : '';
    const remoteSurveyLine = hasRemoteSurveyBlockReason
      ? `<br><small><b>${escapePopupHtml(this.translate.instant('REMOTE_SURVEY_BLOCKERS'))}</b></small>`
      : '';

    let cropSectionHtml: string;
    let plannedCropRow = '';

    if (this.planFactDisplayMode === 'both') {
      const plan =
        this.resolvePlanSeason(season) ?? (seasonAny.isPlanned === true ? season : null);
      if (plan) {
        plannedCropRow =
          `<br><small>${escapePopupHtml(this.translate.instant('PLANNED_CROP_ID'))}: ${this.plannedCropDisplayName(plan)}</small>`;
        cropSectionHtml = this.buildFactCropRowsHtml(this.getLinkedFactSeasons(plan.id));
      } else if (this.isFactSeason(season)) {
        plannedCropRow =
          `<br><small>${escapePopupHtml(this.translate.instant('PLANNED_CROP_ID'))}: ${escapePopupHtml('—')}</small>`;
        cropSectionHtml = this.buildFactCropRowsHtml([season]);
      } else {
        plannedCropRow =
          `<br><small>${escapePopupHtml(this.translate.instant('PLANNED_CROP_ID'))}: ${this.plannedCropDisplayName(season)}</small>`;
        cropSectionHtml = this.buildFactCropRowsHtml(this.getLinkedFactSeasons(season.id));
      }
    } else {
      plannedCropRow =
        this.planFactDisplayMode !== 'fact'
          ? `<br><small>${escapePopupHtml(this.translate.instant('PLANNED_CROP_ID'))}: ${this.plannedCropDisplayName(season)}</small>`
          : '';
      const displayCropId =
        seasonAny.isPlanned && !season.cropId
          ? this.getSeasonPlannedCropId(season)
          : season.cropId;
      cropSectionHtml =
        `<br><small>${escapePopupHtml(this.translate.instant('CROP_ID'))}: ${this.cropDisplayName(displayCropId as string | undefined)}</small>`;
    }

    return (
      `<b>${escapePopupHtml(title)}</b><br>` +
      (this.planFactDisplayMode === 'both' ? plannedCropRow + cropSectionHtml : cropSectionHtml + plannedCropRow) +
      planFactLine +
      `<br><small>${monLabel}: ${monVal}</small>` +
      manualSurveyLine +
      remoteSurveyLine +
      `<br><small>${escapePopupHtml(this.translate.instant('FIELD_AREA'))}: ${this.formatPopupAreaHa(season.fieldArea)} ${haUnit}</small>`
    );
  }


  /** Any in-flight geojson-vt paint from a previous frame is aborted; unblock waiters tied to its settle seq. */
  private flushAbortedVtSettlement(): void {
    if (this.vtSettleSeqPending == null) {
      return;
    }
    const s = this.vtSettleSeqPending;
    this.vtSettleSeqPending = null;
    this.emitPaintSettled(s);
  }

  private emitPaintSettled(seq: number): void {
    this.lastPaintSettledSeq = Math.max(this.lastPaintSettledSeq, seq);
    this.ngZone.run(() => this.paintSettled$.next(seq));
  }

  /**
   * Redraw from in-memory seasons (tiered by zoom).
   */
  private updateDisplay(): void {
    this.flushAbortedVtSettlement();
    const settleSeq = ++this.displayPaintSeq;

    if (!this.mapInstance) {
      this.emitPaintSettled(settleSeq);
      return;
    }

    this.syncZoomFromMapIfPossible();

    const seasonsFiltered = this.filterSeasonsByViewport(this.seasons$.value ?? []);

    this.clearSeasonLayers();

    if (!seasonsFiltered.length) {
      this.emitPaintSettled(settleSeq);
      return;
    }

    if (this.currentZoom < SeasonManager.ZOOM_DETAIL_MIN) {
      this.emitPaintSettled(settleSeq);
      return;
    }

    if (this.currentZoom < SeasonManager.ZOOM_POLYGONS_ONLY_MIN) {
      this.renderSeasonPointsBand(seasonsFiltered);
      this.emitPaintSettled(settleSeq);
      return;
    }

    // Like FieldManager: filled polygons plus clustered centroid markers.
    const vtScheduled = this.renderSeasonPolygonsOnly(seasonsFiltered, settleSeq);
    this.renderSeasonPolygonCentroidMarkersBand(seasonsFiltered);
    if (!vtScheduled) {
      this.emitPaintSettled(settleSeq);
    }
  }

  private syncZoomFromMapIfPossible(): void {
    try {
      const z = this.mapInstance?.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.currentZoom = z;
      }
    } catch {
      /* ignore */
    }
  }

  private filterSeasonsByViewport(seasons: Season[]): Season[] {
    return filterByViewportBounds(
      seasons,
      this.mapInstance?.getMapInstance?.(),
      SEASON_VIEWPORT_BOUNDS_PAD,
      s => this.getSeasonRepresentativeLatLng(s),
    );
  }

  private getSeasonRepresentativeLatLng(season: Season): unknown | null {
    if (season?.polygonJson) {
      const coords = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);
      if (!coords?.length) {
        return null;
      }
      const c = PolygonConverterUtil.getPolygonCenter(coords);
      if (!c || typeof L === 'undefined' || typeof L.latLng !== 'function') {
        return null;
      }
      return L.latLng(c.lat, c.lng);
    }
    if (season.latitude != null && season.longitude != null) {
      return L.latLng(season.latitude, season.longitude);
    }
    return null;
  }

  /** Zoom [12, 14): point markers only (centroid or lat/lng). */
  private renderSeasonPointsBand(seasons: Season[]): void {
    if (!this.mapInstance) return;

    const batchMarkers: any[] = [];
    seasons.forEach(season => {
      if (season.polygonJson) {
        const m = this.markerAtPolygonCentroid(season);
        if (m) {
          batchMarkers.push(m);
          this.managedMarkerIds.add(m.id);
        }
      } else if (season.latitude && season.longitude) {
        const marker = this.createMarkerFromSeason(season);
        if (marker) {
          batchMarkers.push(marker);
          this.managedMarkerIds.add(marker.id);
        }
      }
    });

    if (batchMarkers.length) {
      this.mapInstance.mapController$.next({mergeMarkers: batchMarkers});
    }
  }

  /**
   * Zoom ≥14: centroid markers for polygon seasons (clustered). Point-only seasons are handled in `renderSeasonPolygonsOnly`.
   */
  private renderSeasonPolygonCentroidMarkersBand(seasons: Season[]): void {
    if (!this.mapInstance) return;

    const markers = seasons
      .filter(s => !!s.polygonJson)
      .map(s => this.markerAtPolygonCentroid(s))
      .filter((marker): marker is NonNullable<typeof marker> => marker !== null);

    markers.forEach(m => this.managedMarkerIds.add(m.id));

    if (markers.length) {
      this.mapInstance.mapController$.next({mergeMarkers: markers});
    }
  }

  /** Zoom ≥14: plan/fact stripes via Leaflet polygon, else geojson-vt; non-polygon seasons as markers.
   * @returns Whether geojson-vt rendering was scheduled — when true, `emitPaintSettled` runs from the VT layer lifecycle.
   */
  private renderSeasonPolygonsOnly(seasons: Season[], settleSeq: number): boolean {
    if (!this.mapInstance) {
      return false;
    }

    const lonePointMarkers: any[] = [];
    seasons.forEach(season => {
      if (season.polygonJson) {
        if (this.shouldRenderSeasonStripePolygon(season)) {
          this.addSeasonPolygonShapeOnly(season);
        }
      } else if (season.latitude != null && season.longitude != null) {
        const marker = this.createMarkerFromSeason(season);
        if (marker) {
          lonePointMarkers.push(marker);
          this.managedMarkerIds.add(marker.id);
        }
      }
    });

    const solidForVt = seasons.filter(s => !!s.polygonJson && !this.shouldRenderSeasonStripePolygon(s));

    let vtScheduled = false;
    if (solidForVt.length) {
      vtScheduled = this.renderSeasonPolygonsVectorTiles(solidForVt, settleSeq);
    }

    if (lonePointMarkers.length) {
      this.mapInstance.mapController$.next({mergeMarkers: lonePointMarkers});
    }

    return vtScheduled;
  }

  /** Striped fill when plan+fact mode, both crops set, and they match (mismatch → solid vivid red). */
  private shouldRenderSeasonStripePolygon(season: Season): boolean {
    if (this.planFactDisplayMode !== 'both') {
      return false;
    }
    if (!this.hasSeasonPlannedCropId(season)) {
      return false;
    }
    if (this.getPlanFactCropRelation(season) !== 'match') {
      return false;
    }
    // В новой модели плановый season может не иметь cropId — проверяем наличие фактической культуры
    // через связанные фактические season'ы в текущей выборке
    return this.hasSeasonCropId(season) || this.getLinkedFactSeasons(season.id).length > 0;
  }

  private removeSeasonsVectorTileLayer(): void {
    this.seasonsVtOpId++;
    const leaflet = this.mapInstance?.getMapInstance?.();
    if (this.seasonsVtLayer && leaflet) {
      try {
        leaflet.removeLayer(this.seasonsVtLayer);
      } catch {
        /* ignore */
      }
    }
    this.seasonsVtLayer = null;
    this.seasonsVtPointer = null;
  }

  private ensureSeasonsVtPane(map: any): void {
    const name = 'seasonsVectorTilesPane';
    if (!map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.zIndex = SEASON_VECTOR_TILE_PANE_Z_INDEX;
      pane.style.pointerEvents = 'none';
    }
  }

  /** Solid-fill seasons as geojson-vt (culture color per feature). */
  private buildSeasonsVtFeatureCollection(
    seasons: Season[],
  ): GeoJSON.FeatureCollection | null {
    const features: GeoJSON.Feature[] = [];
    for (const season of seasons) {
      if (!season?.polygonJson || season.id == null) {
        continue;
      }
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);
      if (!PolygonConverterUtil.isValidPolygon(coordinates)) {
        continue;
      }

      const ringLngLat = coordinates.map(([lat, lng]) => [lng, lat] as [number, number]);
      const fr = ringLngLat[0];
      const lr = ringLngLat[ringLngLat.length - 1];
      if (fr[0] !== lr[0] || fr[1] !== lr[1]) {
        ringLngLat.push([fr[0], fr[1]]);
      }

      const seasonVtId = this.seasonVtNumericId(season);
      const surveyBlocked = this.seasonPolygonSurveyBlocked(season);
      const fillColor = surveyBlocked
        ? SEASON_SURVEY_BLOCKED_POLYGON_FILL
        : this.resolveSeasonPolygonFillColor(season);
      const strokeColor = this.resolveSeasonPolygonStrokeColor(season);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [ringLngLat],
        },
        properties: {
          seasonVtId,
          fillColor,
          strokeColor,
          fillOpacity: 0.8,
          strokeWidth: 2,
          _season: season,
        },
      });
    }

    if (!features.length) {
      return null;
    }

    return {type: 'FeatureCollection', features};
  }

  private renderSeasonPolygonsVectorTiles(seasons: Season[], settleSeq: number): boolean {
    const leafletMap = this.mapInstance?.getMapInstance?.();
    if (!leafletMap) {
      return false;
    }

    const fc = this.buildSeasonsVtFeatureCollection(seasons);
    if (!fc?.features?.length) {
      return false;
    }

    const opId = ++this.seasonsVtOpId;
    this.vtSettleSeqPending = settleSeq;
    const featureCount = fc.features.length;
    const mgr = this;

    this.ngZone.runOutsideAngular(() => {
      try {
        const tolerance = featureCount > 20000 ? 5 : 3;
        const tileIndex = geojsonvt(fc as any, {
          maxZoom: SEASON_GEOJSON_VT_OPTIONS.maxZoom,
          extent: SEASON_GEOJSON_VT_OPTIONS.extent,
          buffer: SEASON_GEOJSON_VT_OPTIONS.buffer,
          lineMetrics: SEASON_GEOJSON_VT_OPTIONS.lineMetrics,
          promoteId: SEASON_GEOJSON_VT_OPTIONS.promoteId,
          generateId: SEASON_GEOJSON_VT_OPTIONS.generateId,
          indexMaxZoom: SEASON_GEOJSON_VT_OPTIONS.indexMaxZoom,
          indexMaxPoints: SEASON_GEOJSON_VT_OPTIONS.indexMaxPoints,
          tolerance,
        });

        const featuresById = buildVtOriginalFeaturesByNumericId(fc.features as any[], 'seasonVtId');

        const layer = mgr.createSeasonsVectorGridLayer(tileIndex, featureCount);
        const vtPtr = {tileIndex, featuresById};

        mgr.ngZone.run(() => {
          if (opId !== mgr.seasonsVtOpId) {
            mgr.resolveVtSupersededSettle(settleSeq);
            return;
          }
          const map = mgr.mapInstance?.getMapInstance?.();
          if (!map) {
            mgr.resolveVtPaintFailureSettle(settleSeq);
            return;
          }
          mgr.ensureSeasonsVtPane(map);
          map.addLayer(layer);
          mgr.seasonsVtLayer = layer;
          mgr.seasonsVtPointer = vtPtr;
          mgr.setupSeasonVectorTileLayerSettle(layer, settleSeq, opId);
        });
      } catch (e) {
        console.warn('SeasonManager: geojson-vt rendering failed', e);
        mgr.ngZone.run(() => mgr.resolveVtPaintFailureSettle(settleSeq));
      }
    });
    return true;
  }

  private resolveVtSupersededSettle(settleSeq: number): void {
    if (this.vtSettleSeqPending === settleSeq) {
      this.vtSettleSeqPending = null;
      this.emitPaintSettled(settleSeq);
    }
  }

  private resolveVtPaintFailureSettle(settleSeq: number): void {
    if (this.vtSettleSeqPending === settleSeq) {
      this.vtSettleSeqPending = null;
    }
    this.emitPaintSettled(settleSeq);
  }

  /** One-shot settle once tiles have painted or after a fallback timeout (avoids dangling spinners). */
  private setupSeasonVectorTileLayerSettle(layer: any, settleSeq: number, opId: number): void {
    let done = false;
    const mgr = this;
    let fallbackMs: number | ReturnType<typeof setTimeout>;
    const finish = (): void => {
      if (done) {
        return;
      }
      done = true;
      window.clearTimeout(fallbackMs as any);
      if (mgr.seasonsVtOpId !== opId) {
        return;
      }
      if (mgr.vtSettleSeqPending === settleSeq) {
        mgr.vtSettleSeqPending = null;
      }
      mgr.emitPaintSettled(settleSeq);
    };

    fallbackMs = window.setTimeout(finish, 8000);

    try {
      if (typeof layer?.once === 'function') {
        layer.once('load', () => finish());
      } else {
        window.setTimeout(finish, 0);
      }
    } catch {
      finish();
    }
  }

  private createSeasonsVectorGridLayer(tileIndex: any, featureCount: number): any {
    const thinStroke = featureCount > 20000;

    const VectorTileLayer = L.GridLayer.extend({
      createTile: (coords: any) => {
        const tile = document.createElement('canvas');
        const tileSize = 256;
        tile.width = tileSize;
        tile.height = tileSize;
        tile.style.pointerEvents = 'none';
        const ctx = tile.getContext('2d');
        if (!ctx) return tile;

        const tileData = tileIndex.getTile(coords.z, coords.x, coords.y);
        if (!tileData?.features?.length) return tile;

        const scale = tileSize / SEASON_VT_TILE_EXTENT;
        for (const feature of tileData.features) {
          ctx.save();
          paintVtPolygonOnCanvasTile(
            ctx,
            feature,
            scale,
            thinStroke,
            SEASON_POLYGON_STROKE,
            SEASON_VT_DEFAULT_FILL,
          );
          ctx.restore();
        }
        return tile;
      },
    });

    return new VectorTileLayer({
      pane: 'seasonsVectorTilesPane',
      tileSize: 256,
      updateWhenZooming: false,
      updateWhenIdle: true,
      keepBuffer: 2,
    });
  }

  /** Point season (lat/lng only). */
  private createMarkerFromSeason(season: Season): any {
    if (!season.latitude || !season.longitude) return null;

    return this.buildSeasonMarkerConfig(season, season.latitude, season.longitude, `season_${season.id}`);
  }

  private markerAtPolygonCentroid(season: Season): any | null {
    if (!season.polygonJson || !season.id) {
      return null;
    }
    try {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);
      if (!coordinates?.length) {
        return null;
      }
      const center = PolygonConverterUtil.getPolygonCenter(coordinates);
      if (!center) {
        return null;
      }
      const markerId = `season_polygon_center_${season.id}`;
      return this.buildSeasonMarkerConfig(season, center.lat, center.lng, markerId);
    } catch (e) {
      console.error('Error building season centroid marker', e);
      return null;
    }
  }

  private addSeasonPolygonShapeOnly(season: Season): void {
    if (!this.mapInstance || !season.polygonJson || !season.id) {
      return;
    }

    try {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);

      if (!coordinates || coordinates.length === 0) {
        return;
      }

      // В новой модели план и факт — разные записи; для планового берём cropId из связанного фактического
      let factCropId = season.cropId as string | null | undefined;
      if (!factCropId && (season as any).isPlanned) {
        const linkedFact = this.getLinkedFactSeasons(season.id)[0];
        factCropId = linkedFact?.cropId as string | null | undefined;
      }
      const plannedCropId = this.getSeasonPlannedCropId(season);

      let fillColor: string;
      let strokeColor: string;
      let planFactDiagonalStripes: { factFillColor: string; planStripeColor: string } | undefined;

      if (this.shouldRenderSeasonStripePolygon(season)) {
        fillColor = this.cropCacheService.getCropColorById(String(factCropId), SEASON_VT_DEFAULT_FILL);
        strokeColor = '#ffffff';
        const planStripeColor = this.cropCacheService.getCropColorById(String(plannedCropId), '#9c27b0');
        planFactDiagonalStripes = {
          factFillColor: fillColor,
          planStripeColor: planStripeColor,
        };
      } else {
        fillColor = this.resolveSeasonPolygonFillColor(season);
        strokeColor = this.resolveSeasonPolygonStrokeColor(season);
      }

      if (this.seasonPolygonSurveyBlocked(season)) {
        fillColor = SEASON_SURVEY_BLOCKED_POLYGON_FILL;
        strokeColor = SEASON_SURVEY_BLOCKED_POLYGON_STROKE;
        planFactDiagonalStripes = undefined;
      }

      const polygonId = `season_polygon_${season.id}`;
      const polygon = {
        coordinates: coordinates,
        properties: {
          id: polygonId,
          fillColor: fillColor,
          fillOpacity: 0.8,
          strokeColor: strokeColor,
          color: fillColor,
          strokeWidth: 2,
          fill: true,
          planFactDiagonalStripes,
          data: {
            seasonId: season.id,
            cropId: season.cropId,
            plannedCropId: this.getSeasonPlannedCropId(season),
            fieldId: season.fieldId,
            title: this.getSeasonDisplayTitle(season),
            fieldArea: season.fieldArea,
            ...season
          },
        },
      };

      this.managedPolygonIds.add(polygon.properties.id);
      this.mapInstance.mapController$.next({addPolygon: polygon});
    } catch (error) {
      console.error('Error adding season polygon:', error);
    }
  }

  /** Map pin markup aligned with `map.component.ts` / ground-survey `updateCenterMarker`. */
  private buildGroundSurveyPinHtml(fill: string): string {
    return (
      `<div class="gis-season-marker gis-season-marker--ground-survey-monitoring" style="width:25px;height:41px;line-height:0;">` +
      `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg" style="display:block">` +
      `<path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 2.902 1.004 5.57 2.68 7.682L12.5 41l9.82-20.818C23.996 18.07 25 15.402 25 12.5 25 5.596 19.404 0 12.5 0z" fill="${fill}"/>` +
      `<circle cx="12.5" cy="12.5" r="6" fill="white"/></svg></div>`
    );
  }

  /**
   * Without monitoring: same as fields (`FieldManager`, circle marker from `color` only).
   * With `monitoring: true`: red ground-survey pin plus Leaflet `divIcon` options.
   * Cluster icon: half red / half baseline blue (season map) or green (fields) when monitoring; half vivid red / half baseline when plan vs fact crop differ (monitoring wins if both).
   */
  private buildSeasonMarkerConfig(season: Season, lat: number, lng: number, markerId: string): any {
    const plannedId = this.getSeasonPlannedCropId(season);
    const relation = this.getPlanFactCropRelation(season);
    const monitoring = !!((season as any).monitoring ?? (season as any).isMonitoring);
    const hasManualSurveyBlockReason = this.hasManualSurveyBlockReason(season);
    const planFactCropMismatch = relation === 'mismatch';
    const color = planFactCropMismatch
      ? SEASON_PLAN_FACT_MISMATCH_COLOR
      : this.resolveSeasonCropColor(season);

    const marker: any = {
      id: markerId,
      lat,
      lng,
      title: this.getSeasonDisplayTitle(season),
      popup: this.buildSeasonPopupHtml(season),
      openPopupOnHover: true,
      monitoring,
      hasManualSurveyBlockReason,
      planFactCropMismatch,
      data: {
        seasonId: season.id,
        cropId: season.cropId,
        plannedCropId: plannedId,
        fieldId: season.fieldId,
        startDate: season.startDate,
        endDate: season.endDate,
        fieldArea: season.fieldArea,
        planFactCropRelation: relation,
        monitoring,
        hasManualSurveyBlockReason,
        ...season,
        planFactCropMismatch
      },
      color,
      withCluster: true,
      onClick: () => {
        try {
          const eventsController = this.mapInstance?.getEventsController?.();
          if (eventsController && typeof eventsController.emitMarkerClick === 'function') {
            eventsController.emitMarkerClick({
              id: marker.id,
              lat: marker.lat,
              lng: marker.lng,
              data: marker.data
            });
          }
          this.seasonMarkerClick$.next({
            lat: marker.lat,
            lng: marker.lng,
            data: marker.data
          });
        } catch (err) {
          console.warn('Failed to emit season marker click', err);
        }
      }
    };

    if (monitoring) {
      marker.color = SEASON_MONITORING_GROUND_SURVEY_PIN_FILL;
      marker.customIcon = this.buildGroundSurveyPinHtml(SEASON_MONITORING_GROUND_SURVEY_PIN_FILL);
      marker.customIconSize = [25, 41] as [number, number];
      marker.customIconAnchor = [12, 41] as [number, number];
      marker.customIconPopupAnchor = [1, -34] as [number, number];
    } else if (hasManualSurveyBlockReason) {
      marker.color = SEASON_MANUAL_SURVEY_BLOCK_REASON_PIN_FILL;
      marker.customIcon = this.buildGroundSurveyPinHtml(SEASON_MANUAL_SURVEY_BLOCK_REASON_PIN_FILL);
      marker.customIconSize = [25, 41] as [number, number];
      marker.customIconAnchor = [12, 41] as [number, number];
      marker.customIconPopupAnchor = [1, -34] as [number, number];
    }

    return marker;
  }

  /**
   * API payloads can expose this key in PascalCase or camelCase.
   * If present, marker should use monitoring-like styling.
   */
  private hasManualSurveyBlockReason(season: Season): boolean {
    const seasonAny = season as any;
    const value = seasonAny?.ManualSurveyBlockReasonId ?? seasonAny?.manualSurveyBlockReasonId;
    return value != null && String(value).trim().length > 0;
  }

  private hasRemoteSurveyBlockReason(season: Season): boolean {
    const seasonAny = season as any;
    const value = seasonAny?.RemoteSurveyBlockReasonId ?? seasonAny?.remoteSurveyBlockReasonId;
    return value != null && String(value).trim().length > 0;
  }

  /** Gray / dark polygon when either survey block reason id is set. */
  private seasonPolygonSurveyBlocked(season: Season): boolean {
    return this.hasManualSurveyBlockReason(season) || this.hasRemoteSurveyBlockReason(season);
  }

  private hasSeasonCropId(season: Season): boolean {
    const id = season.cropId as string | null | undefined;
    return id != null && String(id).length > 0;
  }

  private hasSeasonPlannedCropId(season: Season): boolean {
    const id = this.getSeasonPlannedCropId(season);
    return id != null && String(id).length > 0;
  }

  private resolveSeasonPolygonFillColor(season: Season): string {
    if (this.getPlanFactCropRelation(season) === 'mismatch') {
      return SEASON_PLAN_FACT_MISMATCH_COLOR;
    }
    return this.resolveSeasonCropColor(season);
  }

  private resolveSeasonPolygonStrokeColor(season: Season): string {
    if (this.seasonPolygonSurveyBlocked(season)) {
      return SEASON_SURVEY_BLOCKED_POLYGON_STROKE;
    }
    if (this.getPlanFactCropRelation(season) === 'mismatch') {
      return SEASON_PLAN_FACT_MISMATCH_STROKE;
    }
    return SEASON_POLYGON_STROKE;
  }

  private resolveSeasonCropColor(season: Season): string {
    const fallback = SEASON_VT_DEFAULT_FILL;
    if (this.planFactDisplayMode === 'plan') {
      const pid = this.getSeasonPlannedCropId(season);
      return pid != null
        ? this.cropCacheService.getCropColorById(String(pid), fallback)
        : fallback;
    }
    const cid = season.cropId as string | null | undefined;
    if (cid != null && String(cid).length > 0) {
      return this.cropCacheService.getCropColorById(String(cid), fallback);
    }
    if (this.planFactDisplayMode === 'both') {
      const pid = this.getSeasonPlannedCropId(season);
      if (pid != null && String(pid).length > 0) {
        return this.cropCacheService.getCropColorById(String(pid), fallback);
      }
    }
    return fallback;
  }

  private getPlanFactCropRelation(season: Season): 'match' | 'mismatch' | 'none' {
    const plan = this.resolvePlanSeason(season);
    if (!plan) {
      return 'none';
    }

    const plannedCropId = this.getSeasonPlannedCropId(plan);
    if (!plannedCropId) {
      return 'none';
    }

    const factSeasons = this.getLinkedFactSeasons(plan.id);
    if (factSeasons.length === 0) {
      return 'none';
    }

    const hasMatch = factSeasons.some(s => String(s.cropId) === String(plannedCropId));
    return hasMatch ? 'match' : 'mismatch';
  }

  private buildPlanFactPopupLines(season: Season): string {
    if (this.planFactDisplayMode !== 'both') {
      return '';
    }
    const rel = this.getPlanFactCropRelation(season);
    if (rel === 'match') {
      return `<br><small style="color:#2e7d32">${escapePopupHtml(this.translate.instant('PLAN_FACT_CROP_MATCH'))}</small>`;
    }
    if (rel === 'mismatch') {
      return `<br><small style="color:${SEASON_PLAN_FACT_MISMATCH_COLOR}">${escapePopupHtml(this.translate.instant('PLAN_FACT_CROP_DIFFERENT'))}</small>`;
    }
    return '';
  }

  /**
   * Clear all season layers (markers and polygons)
   */
  clearSeasonLayers(): void {
    if (!this.mapInstance) return;

    this.removeSeasonsVectorTileLayer();

    const ids = Array.from(this.managedMarkerIds);
    if (ids.length) {
      this.mapInstance.mapController$.next({removeMarkers: ids});
    }
    this.managedMarkerIds.clear();

    const polygonIds = Array.from(this.managedPolygonIds);
    if (polygonIds.length) {
      this.mapInstance.mapController$.next({removePolygons: polygonIds as string[]});
    }
    this.managedPolygonIds.clear();
  }

  /**
   * Get all managed marker IDs
   */
  getSeasonMarkerIds(): string[] {
    return Array.from(this.managedMarkerIds);
  }

  /**
   * Get all managed polygon IDs
   */
  getSeasonPolygonIds(): (string | number)[] {
    return Array.from(this.managedPolygonIds);
  }

  /**
   * Clear seasons from state
   */
  clearSeasons(): void {
    this.seasons$.next([]);
    this.clearSeasonLayers();
  }

  /**
   * Load seasons by coordinates bounds
   */
  loadSeason(params: {
    west: number,
    east: number,
    south: number,
    north: number,
    query?: {}
  }, center?: boolean): Observable<Field[]> {
    const waitPaintSeq = (seq: number): Observable<unknown> => {
      if (this.lastPaintSettledSeq >= seq) {
        return timer(0);
      }
      return race(
        this.paintSettled$.pipe(filter(s => s === seq), take(1)),
        timer(12000),
      );
    };

    return this.seasonService.cropMonitoringSeasonGetSeasonsByCoordinatesPost(params).pipe(
      mergeMap((fields: Field[]) => {
        const list = fields as Field[];
        this.seasons$.next(list as Season[]);
        const seqAfterData = this.displayPaintSeq;

        return waitPaintSeq(seqAfterData).pipe(
          tap(() => {
            if (center && list?.length) {
              this.centerOnSeasonsOrDefaultZoom(list as Season[]);
            }
          }),
          mergeMap(() => {
            if (!center || !list?.length) {
              return timer(0).pipe(map(() => list));
            }
            /** Let FlyTo / invalidateSize + debounced mapMove (120 ms) bump `displayPaintSeq` before we wait again. */
            return timer(280).pipe(
              mergeMap(() => waitPaintSeq(this.displayPaintSeq)),
              map(() => list),
            );
          }),
        );
      }),
    );
  }

  private centerOnSeasonsOrDefaultZoom(seasons: Season[]): void {
    if (!this.mapInstance || !seasons?.length) return;
    const withPolygon = seasons.find(s => !!s.polygonJson);
    const season =
      withPolygon ?? seasons.find(s => !!(s.latitude && s.longitude));
    if (!season) return;

    if (season.polygonJson) {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);
      const center = coordinates?.length ? PolygonConverterUtil.getPolygonCenter(coordinates) : null;
      if (center) {
        this.mapInstance.mapController$.next({
          setCenter: {lat: center.lat, lng: center.lng, zoom: 16}
        });
      }
    } else if (season.latitude != null && season.longitude != null) {
      this.mapInstance.mapController$.next({
        setCenter: {lat: season.latitude, lng: season.longitude, zoom: 16}
      });
    }
  }

  /**
   * Get regions season count for circle markers.
   * Region scope is passed only via `regionIds`; any `filters` entries with `filterField === 'regionId'` are moved there and stripped from `query`.
   */
  getRegionsSeasonCount(query?: Record<string, unknown>): Observable<GisCircleMarker[]> {
    // let regionIds = this.regionManager.getCachedRegionIds();
    // let requestQuery: Record<string, unknown> | undefined = query;

    let regionIds = this.regionManager.getCachedRegionIds();

    // Check if there's a regionId filter in the query
    // @ts-ignore
    if (query && query['filters'] && Array.isArray(query['filters'])) {
      // @ts-ignore
      const regionFilter = query['filters'].find((filter: any) =>
        filter.filterField === 'regionId'
      );

      if (regionFilter && regionFilter.filterValue) {
        regionIds = regionFilter.filterValue;
      }
    }
    // const rawFilters =
    //   query && Array.isArray((query as {'filters'?: unknown}).filters)
    //     ? ((query as {filters: unknown[]}).filters as {filterField?: string; filterValue?: unknown}[])
    //     : null;

    // if (rawFilters) {
    //   // const otherFilters = rawFilters.filter(f => f?.filterField !== 'regionId');
    //
    //   regionIds = rawFilters
    //     .filter(
    //       f =>
    //         f?.filterField === 'regionId' &&
    //         f.filterValue != null &&
    //         String(f.filterValue).trim().length > 0,
    //     )
    //     .map(f => String(f.filterValue));
    //   requestQuery = {...query, filters: rawFilters};
    // }

    return this.seasonService.cropMonitoringSeasonGetSeasonsByRegionsPost({
      regionIds,
      query: query,
    }).pipe(
      map(counts => {
        return createRegionCircleMarkers(
          counts,
          this.regionManager.getRegionPolygonsCache(),
          SEASON_VT_DEFAULT_FILL,
          this.mapInstance
        );
      })
    );
  }

  export(params: {}) {
    return this.seasonService.cropMonitoringSeasonExportSeasonsPost(params)
  }
}
